import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../lib/api";

function fmtPct(n) {
  if (n === null || n === undefined) return "—";
  return `${(Number(n) * 100).toFixed(1)}%`;
}
function fmtMoney(v) {
  if (v === null || v === undefined) return "—";
  v = Number(v);
  if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  return `$${Math.round(v).toLocaleString()}`;
}

export default function DataQuality() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [fy, setFy] = useState("");
  const [flag, setFlag] = useState("");
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    apiClient.get("/admin/data-quality").then(r => setSummary(r.data)).catch(e => setErr(e.message));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 50, offset };
    if (fy) params.fy = Number(fy);
    if (flag) params.flag = flag;
    apiClient.get("/admin/data-quality/flagged", { params })
      .then(r => { setRows(r.data.rows); setTotal(r.data.total); })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [fy, flag, offset]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="data-quality-page">
      <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Data Quality</h1>
      <p className="text-sm text-zinc-500 mt-1">
        Reports whose computed operating margin tripped one of the anomaly thresholds. Click a CCN to inspect lineage.
      </p>

      {summary && (
        <div className="mt-6 border border-zinc-200 rounded-lg bg-white">
          <div className="px-4 py-2 border-b border-zinc-200 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
            By fiscal year (thresholds: hard cap |margin|&gt;100%, review |margin|&gt;50%)
          </div>
          <table className="w-full data-table" data-testid="dq-summary-table">
            <thead><tr><th>FY</th><th>Reports w/ G-3</th><th>Hard-capped</th><th>Review</th></tr></thead>
            <tbody>
              {Object.entries(summary.by_fiscal_year || {}).map(([y, v]) => (
                <tr key={y} data-testid={`dq-summary-row-${y}`}>
                  <td className="font-mono">FY{y}</td>
                  <td className="font-mono">{v.reports_with_g3?.toLocaleString()}</td>
                  <td className="font-mono">{v.hard_capped}</td>
                  <td className="font-mono">{v.review}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-8 flex flex-wrap gap-3 items-center" data-testid="dq-filters">
        <label className="text-xs font-mono uppercase text-zinc-500">FY</label>
        <select value={fy} onChange={e => { setFy(e.target.value); setOffset(0); }} className="input-brand max-w-[120px]" data-testid="dq-fy-filter">
          <option value="">all</option>
          {["2020","2021","2022","2023","2024"].map(y => <option key={y} value={y}>FY{y}</option>)}
        </select>
        <label className="text-xs font-mono uppercase text-zinc-500">Flag</label>
        <select value={flag} onChange={e => { setFlag(e.target.value); setOffset(0); }} className="input-brand max-w-[160px]" data-testid="dq-flag-filter">
          <option value="">all</option>
          <option value="hard_capped">hard_capped (&gt;100%)</option>
          <option value="review">review (50–100%)</option>
        </select>
        <span className="text-xs font-mono text-zinc-500 ml-auto" data-testid="dq-total">{total.toLocaleString()} flagged</span>
      </div>

      {err && <div className="mt-3 text-sm text-red-700">{err}</div>}

      <div className="mt-4 border border-zinc-200 rounded-lg bg-white">
        <table className="w-full data-table" data-testid="dq-flagged-table">
          <thead>
            <tr><th>CCN</th><th>FY</th><th>Flag</th><th>Margin (raw)</th><th>Revenue</th><th>Expenses</th><th>Net inc.</th><th>Reason</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="text-center py-6 text-sm text-zinc-500">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-sm text-zinc-400">No flagged reports for this filter.</td></tr>}
            {!loading && rows.map((r, i) => (
              <tr key={i} data-testid={`dq-row-${r.ccn}-${r.fiscal_year}`}>
                <td><Link to={`/hospitals/${r.ccn}`} className="text-emerald-700 font-mono">{r.ccn || "—"}</Link></td>
                <td className="font-mono">FY{r.fiscal_year}</td>
                <td><span className={`badge ${r.quality_flag === "hard_capped" ? "badge-terminated" : "badge-muted"}`}>{r.quality_flag}</span></td>
                <td className="font-mono">{fmtPct(r.raw_margin)}</td>
                <td className="font-mono">{fmtMoney(r.total_operating_revenue)}</td>
                <td className="font-mono">{fmtMoney(r.total_operating_expenses)}</td>
                <td className="font-mono">{fmtMoney(r.net_income_from_service_to_patients)}</td>
                <td className="text-xs text-zinc-600">{r.anomaly_reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button className="btn-ghost text-xs" onClick={() => setOffset(Math.max(0, offset - 50))} disabled={offset === 0} data-testid="dq-prev-btn">Prev</button>
        <span className="text-xs font-mono text-zinc-500">{offset + 1} – {Math.min(offset + 50, total)}</span>
        <button className="btn-ghost text-xs" onClick={() => setOffset(offset + 50)} disabled={offset + 50 >= total} data-testid="dq-next-btn">Next</button>
      </div>
    </div>
  );
}
